import React, { useState } from "react";
import cn from "classnames";
import styles from "./InvoiceCard.module.sass";
import Card from "../../../../components/Card";
import Actions from "../../../../components/Actions";
import { downloadInvoice } from "../../../../services/invoice.service";
import { toast } from "react-toastify";

const InvoiceCard = ({ className, echeanceData, invoiceNumber = 1, folderId }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadInvoice = async () => {
    if (!folderId) {
      toast.error("ID du dossier manquant pour télécharger la facture");
      return;
    }

    setIsDownloading(true);
    try {
      const echeanceNumber = echeanceData?.numero || invoiceNumber;
      await downloadInvoice(folderId, echeanceNumber);
      toast.success(`Facture ${echeanceNumber} téléchargée avec succès`);
    } catch (error) {
      console.error("Erreur lors du téléchargement de la facture:", error);
      toast.error("Erreur lors du téléchargement de la facture");
    } finally {
      setIsDownloading(false);
    }
  };

  const actionsWithHandlers = [
    {
      title: "Télécharger la facture",
      action: handleDownloadInvoice,
      disabled: isDownloading
    },
  ];

  // Formater les données d'échéance depuis le JSON
  const formatEcheanceData = (data) => {
    console.log('Données d\'échéance reçues dans InvoiceCard:', data);
    
    if (!data) {
      return {
        number: invoiceNumber,
        amount: "0,00€",
        status: "À venir",
        period: {
          start: "Non défini",
          end: "Non défini"
        },
        invoiceDate: "À venir"
      };
    }

    // Formater les dates
    const formatDate = (dateString) => {
      if (!dateString) return "Non défini";
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', { 
          day: 'numeric', 
          month: 'short', 
          year: 'numeric' 
        });
      } catch {
        return dateString;
      }
    };

    // Formater le montant
    const formatAmount = (amount) => {
      if (!amount && amount !== 0) return "0,00€";
      const numericAmount = parseFloat(amount);
      if (isNaN(numericAmount)) return "0,00€";
      return `${numericAmount.toLocaleString('fr-FR', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      })}€`;
    };

    // Essayer différents champs possibles pour les données
    const numero = data.numero || data.number || invoiceNumber;
    const montant = data.montantTotal || data.montant || data.amount || 0;
    const dateOuverture = data.dateOuverture || data.date || data.dateFacturation;
    const dateFermeture = data.dateFermeture || data.endDate || dateOuverture;

    return {
      number: numero,
      amount: formatAmount(montant),
      status: getStatusFromData(data),
      period: {
        start: formatDate(dateOuverture),
        end: formatDate(dateFermeture)
      },
      invoiceDate: formatDate(dateOuverture)
    };
  };

  // Déterminer le statut basé sur les données
  const getStatusFromData = (data) => {
    if (!data) return "À venir";
    
    // Logique pour déterminer le statut selon vos critères métier
    if (data.codification && data.codification !== "") {
      return "Payée";
    }
    
    // Vérifier différents champs de date possibles
    const dateField = data.dateOuverture || data.date || data.dateFacturation;
    if (!dateField) return "À venir";
    
    try {
      const currentDate = new Date();
      const echeanceDate = new Date(dateField);
      
      if (isNaN(echeanceDate.getTime())) return "À venir";
      
      if (echeanceDate > currentDate) {
        return "À venir";
      } else if (echeanceDate < currentDate) {
        return "En retard";
      }
      
      return "En attente";
    } catch (error) {
      console.error('Erreur lors de la comparaison des dates:', error);
      return "À venir";
    }
  };

  const formattedData = formatEcheanceData(echeanceData);

  const getStatusBadgeClass = (status) => {
    switch (status.toLowerCase()) {
      case 'payée':
        return 'status-green-dark';
      case 'en attente':
        return 'status-yellow';
      case 'en retard':
        return 'status-red';
      case 'à venir':
        return 'status-blue';
      default:
        return 'status-gray';
    }
  };

  return (
    <Card
      className={cn(styles.card, className)}
      title={`Échéance N° ${formattedData.number}`}
      classTitle="title-blue"
      head={
        <Actions
          className={styles.actions}
          classActionsHead={styles.actionsHead}
          items={actionsWithHandlers}
          small
        />
      }
    >
      <div className={styles.invoiceContent}>
        <div className={styles.amountSection}>
          <span className={styles.amount}>{formattedData.amount}</span>
          <span className={cn('status-type', getStatusBadgeClass(formattedData.status))}>
            {formattedData.status}
          </span>
        </div>
        
        <div className={styles.detailsSection}>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Période concernée</span>
            <span className={styles.detailValue}>
              {formattedData.period.start} - {formattedData.period.end}
            </span>
          </div>
          
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Date de facturation</span>
            <span className={styles.detailValue}>{formattedData.invoiceDate}</span>
          </div>
        </div>
        
      </div>
    </Card>
  );
};

export default InvoiceCard;
